import React, { useState, useMemo } from 'react';
import { useAuth } from '../USER/Auth/AuthContext';
import { addEarning, deductServiceCommission } from '../DB/supabaseServices';
import { useGlobalGPS } from '../GPS/GPSContext';
import Cleave from 'cleave.js/react';
import Swal from 'sweetalert2';
import { 
  FaDollarSign, FaExclamationTriangle, FaInfoCircle, 
  FaMapMarkerAlt, FaUser, FaClock, FaCheck, FaTimes, 
  FaMoneyBillWave, FaMobileAlt, FaUniversity, FaCreditCard, 
  FaRoute, FaPercent 
} from 'react-icons/fa';
import BackButton from '../UI/BackButton';
import './RegisterEarnings.css';

const COMMISSION_RATE = 0.15; // 15% Comisión de la plataforma

const PAYMENT_METHODS = [
  { id: 'Efectivo', label: 'Efectivo', icon: FaMoneyBillWave },
  { id: 'Pago Móvil', label: 'Pago Móvil', icon: FaMobileAlt },
  { id: 'Transferencia', label: 'Transferencia', icon: FaUniversity },
  { id: 'Zelle / Cripto', label: 'Zelle / Cripto', icon: FaCreditCard },
];

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hora' },
  { value: 90, label: '1.5 horas' },
  { value: 120, label: '2 horas' },
];

function RegisterEarnings({ onClose }) {
  const { currentUser } = useAuth();
  const { distanceToday } = useGlobalGPS();

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [mileage, setMileage] = useState('');
  const [clientName, setClientName] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [duration, setDuration] = useState('30');
  const [tip, setTip] = useState('');
  const [notes, setNotes] = useState('');

  // Cálculo de desglose de comisión y ganancia neta en tiempo real
  const financialCalc = useMemo(() => {
    const gross = parseFloat(amount) || 0;
    const tipAmount = parseFloat(tip) || 0;
    const commission = gross * COMMISSION_RATE;
    const net = Math.max(0, gross - commission) + tipAmount;
    return {
      gross,
      commission,
      net,
      tipAmount
    };
  }, [amount, tip]);

  const handleAmountChange = (e) => setAmount(e.target.rawValue);
  const handleMileageChange = (e) => setMileage(e.target.rawValue);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      Swal.fire({
        title: 'Monto requerido',
        text: 'Por favor ingresa el monto cobrado por el servicio.',
        icon: 'warning',
        confirmButtonColor: 'var(--bry-accent)'
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: '¿Confirmar Registro Externo?',
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.6; color: #334155;">
          <p><strong>Monto Cobrado:</strong> $ ${financialCalc.gross.toFixed(2)}</p>
          <p style="color: #ef4444;"><strong>Comisión de Plataforma (15%):</strong> -$ ${financialCalc.commission.toFixed(2)}</p>
          <p style="color: #10b981;"><strong>Tu Ganancia Neta:</strong> $ ${financialCalc.net.toFixed(2)}</p>
          <hr style="margin: 8px 0; border: 0; border-top: 1px solid #e2e8f0;"/>
          <small style="color: #64748b;">La comisión de $${financialCalc.commission.toFixed(2)} será debitada del saldo de tu billetera.</small>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Registrar y Pagar %',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: 'var(--bry-accent)',
      cancelButtonColor: '#64748b'
    });

    if (!confirmResult.isConfirmed) return;

    setLoading(true);
    const uid = currentUser?.uid || currentUser?.id;

    try {
      const manualServiceId = `ext-${Date.now()}`;
      const earningRecord = {
        date: new Date().toISOString(),
        amount: financialCalc.net,
        gross_amount: financialCalc.gross,
        commission: financialCalc.commission,
        mileage: mileage ? parseFloat(mileage) : 0,
        platform: 'Servicio Particular / Fuera de Plataforma',
        payment_method: paymentMethod,
        client_name: clientName || 'Cliente Particular',
        pickup_address: pickupAddress || 'Servicio directo',
        dropoff_address: dropoffAddress || 'Destino',
        tip: financialCalc.tipAmount,
        description: notes || `Carrera particular - ${clientName || 'Cliente'} (${paymentMethod})`,
        duration: duration ? parseInt(duration, 10) : 30
      };

      // 1. Guardar en registro de ganancias
      await addEarning(uid, earningRecord);

      // 2. Deducir la comisión de la billetera del conductor
      if (financialCalc.commission > 0) {
        await deductServiceCommission(manualServiceId, uid, financialCalc.commission).catch(err => {
          console.warn('Aviso deduciendo comisión:', err);
        });
      }

      await Swal.fire({
        title: '¡Servicio Registrado!',
        text: `Ganancia guardada exitosamente. Comisión de $${financialCalc.commission.toFixed(2)} procesada.`,
        icon: 'success',
        confirmButtonColor: 'var(--bry-accent)'
      });

      if (typeof onClose === 'function') {
        onClose();
      } else {
        window.history.back();
      }
    } catch (err) {
      console.error('Error al guardar ganancia:', err);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo guardar el registro: ' + (err.message || 'Error desconocido'),
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-earnings-container animate-fade">
      {/* Header */}
      <div className="re-header mb-3">
        <BackButton />
        <div className="re-header-text">
          <h3 className="re-title">Registrar Servicio Externo</h3>
          <span className="re-subtitle">Declaración de carreras particulares fuera de la plataforma</span>
        </div>
      </div>

      {/* Info Notice */}
      <div className="re-info-banner mb-3">
        <FaInfoCircle className="re-info-icon" />
        <div className="re-info-body">
          <strong>Las carreras dentro de la app se registran automáticamente.</strong>
          <span>Utiliza este formulario únicamente si realizaste un servicio fuera de la plataforma.</span>
        </div>
      </div>

      {/* Warning de Comisión Obligatoria */}
      <div className="re-warning-card mb-4">
        <div className="re-warning-header">
          <FaExclamationTriangle className="re-warning-icon" />
          <span className="re-warning-title">Aviso de Comisión de Plataforma (15%)</span>
        </div>
        <p className="re-warning-desc">
          Al registrar una carrera externa, debes tributar el porcentaje correspondiente a la plataforma. 
          La comisión del <strong>15%</strong> se deducirá automáticamente de tu saldo de billetera.
        </p>

        {/* Real-time breakdown calculator */}
        <div className="re-calc-preview">
          <div className="re-calc-item">
            <span className="re-calc-label">Cobrado al Cliente:</span>
            <span className="re-calc-val font-bold">$ {financialCalc.gross.toFixed(2)}</span>
          </div>
          <div className="re-calc-item text-danger">
            <span className="re-calc-label"><FaPercent size={10} className="me-1" /> Comisión Plataforma (15%):</span>
            <span className="re-calc-val">- $ {financialCalc.commission.toFixed(2)}</span>
          </div>
          <div className="re-calc-divider"></div>
          <div className="re-calc-item text-success">
            <span className="re-calc-label font-bold">Tu Ganancia Neta:</span>
            <span className="re-calc-val font-bold text-lg">+ $ {financialCalc.net.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="re-form-card">
        {/* Monto Cobrado */}
        <div className="re-form-group mb-3">
          <label className="re-form-label">Monto Bruto Cobrado ($ USD) *</label>
          <div className="re-input-wrapper">
            <span className="re-input-icon"><FaDollarSign /></span>
            <Cleave
              options={{
                numeral: true,
                numeralThousandsGroupStyle: 'thousand',
                numeralDecimalScale: 2,
                numeralPositiveOnly: true,
              }}
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
              className="re-input pl-icon"
              required
            />
          </div>
        </div>

        {/* Método de Pago Recibido */}
        <div className="re-form-group mb-3">
          <label className="re-form-label">Método de Pago Recibido *</label>
          <div className="re-payment-grid">
            {PAYMENT_METHODS.map((pm) => {
              const Icon = pm.icon;
              const isSelected = paymentMethod === pm.id;
              return (
                <button
                  key={pm.id}
                  type="button"
                  className={`re-payment-btn ${isSelected ? 'active' : ''}`}
                  onClick={() => setPaymentMethod(pm.id)}
                >
                  <Icon size={14} />
                  <span>{pm.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cliente / Contacto */}
        <div className="re-form-group mb-3">
          <label className="re-form-label">Cliente / Pasajero Atendido</label>
          <div className="re-input-wrapper">
            <span className="re-input-icon"><FaUser /></span>
            <input
              type="text"
              placeholder="Nombre del cliente o 'Particular'"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="re-input pl-icon"
            />
          </div>
        </div>

        {/* Ruta (Origen y Destino) */}
        <div className="re-form-row mb-3">
          <div className="re-form-group flex-1">
            <label className="re-form-label">Punto de Origen</label>
            <div className="re-input-wrapper">
              <span className="re-input-icon text-accent"><FaMapMarkerAlt /></span>
              <input
                type="text"
                placeholder="Ej. Av. Principal / Plaza"
                value={pickupAddress}
                onChange={(e) => setPickupAddress(e.target.value)}
                className="re-input pl-icon"
              />
            </div>
          </div>
          <div className="re-form-group flex-1">
            <label className="re-form-label">Destino</label>
            <div className="re-input-wrapper">
              <span className="re-input-icon text-warning"><FaRoute /></span>
              <input
                type="text"
                placeholder="Ej. Centro Comercial"
                value={dropoffAddress}
                onChange={(e) => setDropoffAddress(e.target.value)}
                className="re-input pl-icon"
              />
            </div>
          </div>
        </div>

        {/* KM y Duración */}
        <div className="re-form-row mb-3">
          <div className="re-form-group flex-1">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="re-form-label m-0">KM Recorridos</label>
              {distanceToday > 0 && (
                <button 
                  type="button"
                  className="re-gps-link" 
                  onClick={() => setMileage(distanceToday.toFixed(1))}
                >
                  Usar GPS ({distanceToday.toFixed(1)} km)
                </button>
              )}
            </div>
            <Cleave
              options={{ numeral: true, numeralDecimalScale: 1 }}
              placeholder="0.0"
              value={mileage}
              onChange={handleMileageChange}
              className="re-input"
            />
          </div>

          <div className="re-form-group flex-1">
            <label className="re-form-label mb-1">Duración Aproximada</label>
            <div className="re-input-wrapper">
              <span className="re-input-icon"><FaClock /></span>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="re-input pl-icon"
              >
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Propina y Notas */}
        <div className="re-form-row mb-4">
          <div className="re-form-group flex-1">
            <label className="re-form-label">Propina Adicional ($)</label>
            <input
              type="number"
              step="0.5"
              placeholder="0.00"
              value={tip}
              onChange={(e) => setTip(e.target.value)}
              className="re-input"
            />
          </div>
          <div className="re-form-group flex-1">
            <label className="re-form-label">Notas / Observaciones</label>
            <input
              type="text"
              placeholder="Detalle opcional..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="re-input"
            />
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="re-actions-row">
          <button 
            type="button" 
            className="bry-btn bry-btn-ghost flex-1 justify-content-center"
            onClick={() => {
              if (typeof onClose === 'function') onClose();
              else window.history.back();
            }}
          >
            <FaTimes className="me-2" /> Cancelar
          </button>
          <button 
            type="submit" 
            className="bry-btn bry-btn-primary flex-1 justify-content-center"
            disabled={loading}
          >
            {loading ? 'Guardando...' : <><FaCheck className="me-2" /> Declarar y Guardar</>}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RegisterEarnings;
